import React, { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Switch,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Template, Worksheet } from '../../types/types';

// Types
export interface TemplateWorksheetRef {
    isDirty: boolean;
    getData: () => Template | null;
    save: () => Promise<Template | null>;
    reset: () => void;
}

interface TemplateWorksheetsProps {
    incomingTemplate: Template;
    onDirtyChange?: (isDirty: boolean) => void;
}

const TemplateWorksheets = forwardRef<TemplateWorksheetRef, TemplateWorksheetsProps>(
    ({ incomingTemplate, onDirtyChange }, ref) => {
        const [template, setTemplate] = useState<Template | null>(incomingTemplate);
        const [originalTemplate, setOriginalTemplate] = useState<Template | null>(
            JSON.parse(JSON.stringify(incomingTemplate))
        );
        const [selectedWorksheet, setSelectedWorksheet] = useState<Worksheet | null>(
            null
        );
        const [openModal, setOpenModal] = useState(false);

        // Compute isDirty by comparing current state with original
        const isDirty = React.useMemo(() => {
            if (!template || !originalTemplate) return false;
            return JSON.stringify(template) !== JSON.stringify(originalTemplate);
        }, [template, originalTemplate]);

        // Notify parent component when dirty state changes
        useEffect(() => {
            onDirtyChange?.(isDirty);
        }, [isDirty, onDirtyChange]);

        // Handle worksheet toggle
        const handleToggleWorksheet = useCallback(
            (id: number) => {
                if (!template) return;

                setTemplate((prev) => {
                    if (!prev) return prev;
                    const worksheets = prev.worksheets?.map((worksheet) =>
                        worksheet.id === id
                            ? {
                                ...worksheet,
                                isEnabled: !worksheet.isEnabled,
                                customName: null
                            }
                            : worksheet
                    ) ?? null;
                    return {
                        ...prev,
                        worksheets,
                    };
                });
            },
            [template]
        );

        // Handle custom name change
        const handleCustomNameChange = useCallback(
            (id: number, value: string) => {
                if (!template) return;

                setTemplate((prev) => {
                    if (!prev) return prev;
                    const worksheets = prev.worksheets?.map((worksheet) =>
                        worksheet.id === id
                            ? { ...worksheet, customName: value } : worksheet
                    ) ?? null;
                    return {
                        ...prev,
                        worksheets,
                    };
                });
            },
            [template]
        );

        // Open edit modal
        const handleOpenModal = useCallback((worksheet: Worksheet) => {
            setSelectedWorksheet(worksheet);
            setOpenModal(true);
        }, []);

        // Close edit modal
        const handleCloseModal = useCallback(() => {
            setOpenModal(false);
            setSelectedWorksheet(null);
        }, []);

        // Save changes
        const saveChanges = useCallback(async () => {
            if (!template) return null;

            // Replace worksheet names with custom names, where provided
            const updatedTemplate = {
                ...template,
                worksheets: template.worksheets?.map((worksheet) => ({
                    ...worksheet,
                    name: worksheet.customName ? worksheet.customName: worksheet.name,
                })) ?? null,
            };

            try {
                const response = await fetch('api/template/saveTemplate', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(updatedTemplate),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const savedTemplate = await response.json();
                setTemplate(savedTemplate);
                setOriginalTemplate(JSON.parse(JSON.stringify(savedTemplate))); // Deep copy
                return savedTemplate;
            } catch (error) {
                console.error('Error saving template:', error);
                throw error;
            }
        }, [template]);

        // Reset changes
        const resetChanges = useCallback(() => {
            if (originalTemplate) {
                setTemplate(JSON.parse(JSON.stringify(originalTemplate))); // Deep copy
            }
        }, [originalTemplate]);

        function isWorksheetEnabled(value: boolean, id: number): boolean {
            // if worksheet(id) is required, it should be enabled
            const worksheet = template?.worksheets?.find((worksheet) => worksheet.id === id);

            if (worksheet && worksheet.isRequired) {
                return true;
            }

            return value;
        }

        function isWorksheetDisabled(id: number): boolean {
            // if worksheet(id) is required, it should be disabled
            const worksheet = template?.worksheets?.find((worksheet) => worksheet.id === id);

            if (worksheet && worksheet.isRequired) {
                return true;	// disabled
            }

            return false;	// enabled
        }

        function isCustomNameDisabled(id: number): boolean {
            const worksheet = template?.worksheets?.find((worksheet) => worksheet.id === id);
            return !worksheet?.isEnabled;
        }

        // DataGrid columns
        const columns: GridColDef[] = [
            {
                field: 'isEnabled',
                headerName: '',
                width: 70,
                sortable: false,
                renderCell: (params: GridRenderCellParams<Worksheet>) => (
                    <Switch
                        checked={isWorksheetEnabled(params.row.isEnabled, params.row.id)}
                        disabled={isWorksheetDisabled(params.row.id)}
                        onChange={() => handleToggleWorksheet(params.row.id)}
                        color="primary"
                    />
                ),
            },
            {
                field: 'name',
                headerName: 'Name',
                flex: 1,
                minWidth: 150,
                sortable: false,
            },
            {
                field: 'customName',
                headerName: 'Custom Name',
                flex: 1,
                minWidth: 200,
                sortable: false,
                renderCell: (params: GridRenderCellParams<Worksheet>) => (
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={params.value || ''}
                        onChange={(e) =>
                            handleCustomNameChange(params.row.id, e.target.value)
                        }
                        onKeyDown={(e) => {
                            // Prevent DataGrid from capturing these keyboard events
                            if (
                                e.key === ' ' || // Space
                                (e.ctrlKey && e.key === 'a') || // Ctrl+A
                                (e.ctrlKey && e.key === 'c') || // Ctrl+C
                                (e.ctrlKey && e.key === 'v') || // Ctrl+V
                                (e.ctrlKey && e.key === 'x')    // Ctrl+X
                            ) {
                                e.stopPropagation();
                            }
                        }}
                        placeholder={params.row.name}
                        disabled={isCustomNameDisabled(params.row.id)}
                    />
                ),
            },
            {
                field: 'options',
                headerName: 'Options',
                width: 120,
                sortable: false,
                renderCell: (params: GridRenderCellParams<Worksheet>) => (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenModal(params.row)}
                    >
                        Edit
                    </Button>
                ),
            },
        ];

        // Expose methods via ref
        useImperativeHandle(ref, () => ({
            isDirty,
            getData: () => template,
            save: saveChanges,
            reset: resetChanges,
        }), [isDirty, template, saveChanges, resetChanges]);

        if (!template) {
            return <Typography>No template found</Typography>;
        }

        return (
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Template Worksheets
                </Typography>

                <TextField
                    fullWidth
                    multiline
                    rows={2}
                    variant="standard"
                    label="Description"
                    value={template.description || ''}
                    onChange={(e) => setTemplate(prev => prev ? { ...prev, description: e.target.value } : prev)}
                    InputLabelProps={{
                        shrink: true,
                        style: {
                            color: "black",
                        },
                    }}
                    sx={{ mt: 2, mb: 2 }}
                />

                <Box sx={{ height: 400, width: '100%' }}>
                    <DataGrid
                        rows={template.worksheets ?? []}
                        columns={columns}
                        disableSelectionOnClick
                        disableColumnMenu
                        density="standard"
                        hideFooter
                        autoHeight
                        sx={{
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#E4EDF0',
                            }
                        }}
                    />
                </Box>

                {/* Worksheet Options Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        Worksheet Options: {selectedWorksheet?.name || ''}
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" paragraph>
                            Additional worksheet options would go here. This could include:
                        </Typography>
                        <ul>
                            <li>Column visibility settings</li>
                            <li>Formatting options</li>
                            <li>Data source configurations</li>
                            <li>Calculation settings</li>
                        </ul>
                        {/* Add more options as needed */}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseModal}>Close</Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        );
    }
);

// Add display name for debugging
TemplateWorksheets.displayName = 'TemplateWorksheets';

export default TemplateWorksheets;
